import { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { personnelApi, errMsg, AlertBadge, targetOf } from './personnelApi';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'PR', 'VI', 'GU', 'AS', 'MP',
];

const blank = (person) => ({
  organizationId: '',
  legalName: person?.name || '',
  npi: '',
  licenseNumber: '',
  licenseState: '',
  licenseExpiration: '',
  dob: '',
  role: person?.roleLabel || '',
  email: person?.email || '',
  dea: '',
  verifyNpi: true,
});

const fromCredential = (c) => ({
  organizationId: c.organizationId,
  legalName: c.legalName || '',
  npi: c.npi || '',
  licenseNumber: c.licenseNumber || '',
  licenseState: c.licenseState || '',
  licenseExpiration: c.licenseExpiration || '',
  dob: '',
  role: c.role || '',
  email: c.email || '',
  dea: '',
  verifyNpi: false,
});

const Revealable = ({ credential, field, masked, canReveal }) => {
  const alert = useAlert();
  const [value, setValue] = useState(null);
  useEffect(() => {
    if (!value) return undefined;
    const t = setTimeout(() => setValue(null), 30000);
    return () => clearTimeout(t);
  }, [value]);
  if (!masked) return <span className="text-muted">Not on file</span>;
  const reveal = async () => {
    try {
      const r = await personnelApi.reveal(credential.id, field);
      setValue(r.value);
    } catch (e) {
      alert.error(errMsg(e));
    }
  };
  return (
    <span>
      <code>{value || masked}</code>{' '}
      {canReveal &&
        (value ? (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setValue(null)}>Hide</button>
        ) : (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={reveal} title="Reveal is audit-logged">
            Reveal
          </button>
        ))}
      {value && <small className="text-muted"> (audited)</small>}
    </span>
  );
};

const CredentialForm = ({ initial, editing, orgOptions, onCancel, onSubmit }) => {
  const alert = useAlert();
  const [f, setF] = useState(initial);
  const [npiCheck, setNpiCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const checkNpi = async () => {
    setNpiCheck({ loading: true });
    try {
      setNpiCheck(await personnelApi.lookupNpi(f.npi));
    } catch (e) {
      setNpiCheck({ error: errMsg(e) });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f };
    if (editing) {
      delete body.organizationId;
      if (!body.dob) delete body.dob;
      if (!body.dea) delete body.dea;
    }
    setSaving(true);
    try {
      await onSubmit(body);
    } catch (err) {
      alert.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} autoComplete="off" className="p-3 mt-2" style={{ background: '#f8fafc', border: '1px solid #dee2e6', borderRadius: 6 }}>
      <div className="row">
        {!editing && (
          <div className="col-md-4 mb-2">
            <label className="form-label mb-0">Organization *</label>
            <select className="form-control" value={f.organizationId} onChange={set('organizationId')} required>
              <option value="">Select organization</option>
              {orgOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="col-md-4 mb-2">
          <label className="form-label mb-0">Full legal name *</label>
          <input className="form-control" value={f.legalName} onChange={set('legalName')} required />
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label mb-0">Individual NPI (Type 1) *</label>
          <div className="d-flex">
            <input className="form-control" value={f.npi} onChange={set('npi')} maxLength={10} inputMode="numeric" required />
            <button type="button" className="btn btn-outline-secondary btn-sm ms-1" onClick={checkNpi} disabled={!f.npi}>
              Check
            </button>
          </div>
          {npiCheck && (
            <small className={npiCheck.error || npiCheck.isIndividual === false ? 'text-danger' : 'text-muted'}>
              {npiCheck.loading && 'Checking NPPES...'}
              {npiCheck.error}
              {npiCheck.available === false && 'Check digit OK; NPPES registry unreachable.'}
              {npiCheck.available && !npiCheck.found && 'Check digit OK; not found in NPPES.'}
              {npiCheck.found && `${npiCheck.name || ''} — ${npiCheck.isIndividual ? 'Type 1 (individual)' : 'Type 2 (organization): not allowed'}`}
            </small>
          )}
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label mb-0">License number</label>
          <input className="form-control" value={f.licenseNumber} onChange={set('licenseNumber')} autoComplete="off" />
        </div>
        <div className="col-md-2 mb-2">
          <label className="form-label mb-0">Issuing state</label>
          <select className="form-control" value={f.licenseState} onChange={set('licenseState')}>
            <option value="">--</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label mb-0">License expiration</label>
          <input type="date" className="form-control" value={f.licenseExpiration} onChange={set('licenseExpiration')} />
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label mb-0">DOB {editing && <small className="text-muted">(blank = unchanged)</small>}</label>
          <input type="date" className="form-control" value={f.dob} onChange={set('dob')} autoComplete="off" />
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label mb-0">Role</label>
          <input className="form-control" value={f.role} onChange={set('role')} />
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label mb-0">Email</label>
          <input type="email" className="form-control" value={f.email} onChange={set('email')} />
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label mb-0">DEA number (optional){editing && <small className="text-muted"> blank = unchanged</small>}</label>
          <input type="password" className="form-control" value={f.dea} onChange={set('dea')} autoComplete="new-password" maxLength={11} />
        </div>
      </div>
      <div className="form-check">
        <input id="cred-verify" type="checkbox" className="form-check-input" checked={!!f.verifyNpi} onChange={set('verifyNpi')} />
        <label htmlFor="cred-verify" className="form-check-label">Confirm NPI with the NPPES registry on save</label>
      </div>
      <div className="mt-2">
        <button type="submit" className="edit-pill" disabled={saving}>{saving ? 'Saving...' : editing ? 'Save credential' : 'Add credential'}</button>{' '}
        <button type="button" className="btn btn-link btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

// Credentialing roster for one person (one record per organization). Super Admin edits; Org Manager views.
const CredentialSection = ({ person, credentials, organizations, permissions, onChanged }) => {
  const alert = useAlert();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const canEdit = !!permissions?.canEditCredentials;

  const used = new Set(credentials.map((c) => c.organizationId));
  const eligibleOrgs = organizations.filter(
    (o) => o.status === 'active' && !used.has(o.id) && (!person.organizationIds.length || person.organizationIds.includes(o.id))
  );

  const create = async (body) => {
    await personnelApi.createCredential(person.kind, person.id, body, targetOf(person));
    alert.success('Credential added');
    setAdding(false);
    onChanged();
  };
  const update = (id) => async (body) => {
    await personnelApi.updateCredential(id, body);
    alert.success('Credential updated');
    setEditingId(null);
    onChanged();
  };

  return (
    <div className="p-3 mt-3" style={{ border: '1px solid #dee2e6', borderRadius: 6 }}>
      <div className="d-flex justify-content-between align-items-center">
        <h6 style={{ color: '#004aad', margin: 0 }}>
          <i className="fas fa-certificate"></i> Credentialing {!canEdit && <small className="text-muted">(view only)</small>}
        </h6>
        {canEdit && !adding && eligibleOrgs.length > 0 && (
          <button type="button" className="edit-pill" onClick={() => setAdding(true)}>+ Add credential</button>
        )}
      </div>
      {adding && (
        <CredentialForm initial={blank(person)} orgOptions={eligibleOrgs} onCancel={() => setAdding(false)} onSubmit={create} />
      )}
      {credentials.length === 0 && !adding && <small className="text-muted d-block mt-2">No credential records on file.</small>}
      {credentials.map((c) =>
        editingId === c.id ? (
          <CredentialForm key={c.id} editing initial={fromCredential(c)} orgOptions={[]} onCancel={() => setEditingId(null)} onSubmit={update(c.id)} />
        ) : (
          <div key={c.id} className="mt-2 p-2" style={{ borderTop: '1px solid #eee' }}>
            <div className="d-flex justify-content-between">
              <b>{c.organizationName || 'Organization'}</b>
              <span>
                <AlertBadge level={c.alertLevel} days={c.daysUntilExpiration} />{' '}
                {canEdit && (
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setEditingId(c.id)}>Edit</button>
                )}
              </span>
            </div>
            <div className="row" style={{ fontSize: 14 }}>
              <div className="col-md-4">Legal name: <b>{c.legalName}</b></div>
              <div className="col-md-4">
                NPI: <b>{c.npi || '-'}</b>{' '}
                {c.npiVerification && (
                  <small className="text-muted">
                    ({c.npiVerification.found ? `NPPES ${c.npiVerification.type || ''}` : c.npiVerification.available ? 'not in NPPES' : 'NPPES unreachable'})
                  </small>
                )}
              </div>
              <div className="col-md-4">Role: {c.role || '-'}</div>
              <div className="col-md-4">License: {c.licenseNumber || '-'} {c.licenseState ? `(${c.licenseState})` : ''}</div>
              <div className="col-md-4">Expires: {c.licenseExpiration || '-'}</div>
              <div className="col-md-4">Email: {c.email || '-'}</div>
              <div className="col-md-4">
                DOB: <Revealable credential={c} field="dob" masked={c.dobMasked} canReveal={permissions?.canReveal} />
              </div>
              <div className="col-md-4">
                DEA: <Revealable credential={c} field="dea" masked={c.deaMasked} canReveal={permissions?.canReveal} />
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default CredentialSection;
